<template>
  <teleport to="body">
    <div
      class="overlay"
      v-if="modelValue"
      @click="onOverlayClick"
      :style="overlayStyle"
    >
      <div
        class="sheet"
        :style="{ transform: `translateY(${translateY}px)` }"
        @pointerdown="startDrag"
      >
        <div class="grip"></div>
        <div class="slot">
        <slot />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: "SheetModal",
  props: {
    modelValue: { type: Boolean, required: true },
    dragThreshold: { type: Number, default: 100 } // Порог в пикселях
  },
  emits: ["update:modelValue"],
  data() {
    return {
      startY: 0,
      lastY: 0,
      translateY: 0,
      dragging: false,
      dragActivated: false, // Новое состояние
      lastMoveTime: 0,
      lastMoveDelta: 0,
      windowHeight: 0,
      maxBlur: 8,
      overlayOpacity: 0,
      wasDragging: false
    }
  },
  watch: {
    modelValue(v) {
      if (v) {
        this.lockScroll()
        this.open()
      } else {
        this.unlockScroll()
      }
    }
  },
  computed: {
    overlayStyle() {
      const blur = this.maxBlur * (1 - this.translateY / this.windowHeight)
      return {
        backdropFilter: `blur(${blur}px)`,
        opacity: this.overlayOpacity
      }
    }
  },
  mounted() {
    this.windowHeight = window.innerHeight
    this._isUnmounted = false
    if (this.modelValue) this.lockScroll()
  },
  beforeUnmount(){
    this._isUnmounted = true
    // Cleanup drag listeners
    window.removeEventListener("pointermove", this.onDrag)
    window.removeEventListener("pointerup", this.endDrag)
    // Reset dragging state
    this.dragging = false
    this.dragActivated = false
    // Restore scroll if was locked
    this.unlockScroll()
  },
  methods: {
    lockScroll() { document.body.style.overflow = "hidden" },
    unlockScroll() { document.body.style.overflow = "" },

    open() {
      this.windowHeight = window.innerHeight
      this.translateY = this.windowHeight
      this.overlayOpacity = 0

      requestAnimationFrame(() => {
        if (this._isUnmounted) return
        this.$nextTick(() => {
          if (this._isUnmounted) return
          requestAnimationFrame(() => {
            if (this._isUnmounted) return
            this.translateY = 0
            this.overlayOpacity = 1
          })
        })
      })
    },

    close() {
      this.translateY = this.windowHeight
      this.overlayOpacity = 0
      setTimeout(() => {
        this.$emit("update:modelValue", false)
        this.translateY = 0
        this.overlayOpacity = 0
      }, 220)
    },

    startDrag(e) {
      if (e.pointerType === "mouse" && e.button !== 0) return

      // Игнорируем drag если клик был на EqComponent
      if (e.target.closest(".eq-component")) return

      this.dragging = true
      this.dragActivated = false // Сброс состояния
      this.wasDragging = false
      this.startY = e.clientY
      this.lastY = e.clientY
      this.lastMoveTime = performance.now()

      e.target.setPointerCapture?.(e.pointerId)

      window.addEventListener("pointermove", this.onDrag)
      window.addEventListener("pointerup", this.endDrag)
    },

    onDrag(e) {
      if (!this.dragging) return

      const y = e.clientY
      const delta = y - this.startY

      // Активируем drag только после преодоления порога
      if (!this.dragActivated) {
        if (delta > this.dragThreshold) {
          this.dragActivated = true
          this.wasDragging = true
        } else {
          return // Игнорируем движение до достижения порога
        }
      }

      // Применяем translateY только для движения вниз
      this.translateY = Math.max(0, delta)

      const dt = performance.now() - this.lastMoveTime
      const dy = y - this.lastY
      this.lastMoveDelta = dy / dt

      this.lastY = y
      this.lastMoveTime = performance.now()
    },

    endDrag(e) {
      if (!this.dragging) return
      this.dragging = false

      e.target.releasePointerCapture?.(e.pointerId)

      window.removeEventListener("pointermove", this.onDrag)
      window.removeEventListener("pointerup", this.endDrag)

      // Закрываем только если drag был активирован
      if (this.dragActivated) {
        const shouldClose = this.translateY > 120 || this.lastMoveDelta > 0.35
        if (shouldClose) this.close()
        else this.translateY = 0
      } else {
        this.translateY = 0
      }

      this.dragActivated = false
    },

    onOverlayClick(e) {
      if (!this.wasDragging && e.target === e.currentTarget) {
        this.close()
      }
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  height: 100dvh;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: flex-end;
  transition: backdrop-filter 150ms ease, opacity 150ms ease;
  z-index: 1000;
}

.sheet {
  width: 100%;
  height: 90vh;
  background: rgba(26, 26, 26, 0.9);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12.3px);
  -webkit-backdrop-filter: blur(12.3px);
  border: 1px solid rgba(26, 26, 26, 0.61);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  color: white;
  overflow: hidden;
  transition: transform 500ms cubic-bezier(.2,.8,.2,1);
  will-change: transform;
  display: flex;
  flex-direction: column;
}

.grip {
  width: 36px;
  height: 5px;
  border-radius: 3px;
  background: rgba(255,255,255,0.2);
  margin: 10px auto;
  flex-shrink: 0;
  cursor: grab;
}

.grip:active {
  cursor: grabbing;
}

.slot {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}
</style>